import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  HostListener,
  inject,
  input,
  linkedSignal,
  OnInit,
  output,
  signal,
  TemplateRef,
  viewChild,
  viewChildren,
  WritableSignal,
} from '@angular/core';
import { PreviewToolBarComponent } from './preview-tool-bar/preview-tool-bar.component';
import { WaIntersectionObserver } from '@ng-web-apis/intersection-observer';
import { PreviewThumbnailBarComponent } from './thumbnail-bar/preview-thumbnail-bar.component';
import { NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import { FitType } from './model/fit-type.enum';
import {
  DocumentPagePreviewComponent,
  PageIntersectionEvent,
} from './document-page-preview/document-page-preview.component';
import { DocumentPreviewSeparatorComponent } from './document-preview-separator/document-preview-separator.component';
import { filter, fromEvent, map, switchMap, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PagePreviewSeparatorComponent } from './page-preview-separator/page-preview-separator.component';
import { associateBy } from '@ps-gui/shared-utils';
import { PageTracker } from './service/page-tracker.service';
import {
  CurrentPage,
  fixDocumentClass,
  SelectInitialPageBehaviour,
} from './model/document-preview.utils';
import { PSDocumentPage } from './model/document-page.interface';
import { PSDocument } from './model/document.interface';
import { PageImageService } from './service/page-image.service';
import { DocumentSearchService } from './service/document-search.service';
import { SearchQuery } from './model/document-searcher.interface';
import { SearchResultOverlayComponent } from './search-result-overlay/search-result-overlay.component';
import { SearchResultNavigator } from './service/search-result-navigator';
import { BehandlerItem } from './model/behandler-item.interface';
import { NONE_MEDICAL_BEHANDLER_ITEM } from './thumbnail-bar/document-class-filter/document-filter.component';

@Component({
  selector: 'shared-dp-document-preview',
  templateUrl: './document-preview.component.html',
  imports: [
    PreviewToolBarComponent,
    WaIntersectionObserver,
    PreviewThumbnailBarComponent,
    NgClass,
    NgStyle,
    NgTemplateOutlet,
    DocumentPagePreviewComponent,
    DocumentPreviewSeparatorComponent,
    SearchResultOverlayComponent,
    PagePreviewSeparatorComponent,
  ],
  providers: [PageTracker],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentPreviewComponent implements OnInit {
  readonly pageOverlayTemplate = input<TemplateRef<{ $implicit: PSDocumentPage }>>();
  readonly documentLabelTemplate = input<TemplateRef<{ $implicit: PSDocument }>>();
  readonly documentThumbnailOverlayTemplate = input<TemplateRef<{ $implicit: PSDocument }>>();
  readonly documentSeparatorLabelTemplate =
    input<TemplateRef<{ $implicit: PSDocument; index: number }>>();
  readonly pageSeparatorLabelTemplate =
    input<TemplateRef<{ $implicit: PSDocument; splitIndex: number }>>();
  readonly splitFunctionality = input<boolean>(false);
  readonly documentClassFilterEnabled = input(false);
  readonly isPreviewListExpandedInitially = input(false);
  readonly searchableDocumentIds = input<string[]>();
  readonly singleOpenedDocument = input<PSDocument | undefined>(undefined);
  /**
   * This property can be disabled to "decouple" scrolling in the preview from the currentPage
   * signal which controls selection of the miniature page.
   */
  readonly changeActivePageOnScroll = input(true);
  readonly scrollToFirstPageOnInit = input(true);
  readonly allDocuments = input.required<PSDocument[]>();
  readonly selectInitialPageBehaviour = input<SelectInitialPageBehaviour>('first');
  readonly documentIdByPageId = input<Record<string, string> | undefined>(undefined);

  readonly imageContainerScrollEnd = output<void>();
  readonly documentThumbnailSelected = output<string>();
  readonly documentSelected = output<string>();
  readonly pageSelected = output<string>();

  private readonly pageImageContainer =
    viewChild.required<ElementRef<HTMLDivElement>>('pageImageContainer');
  private readonly pageImageElements = viewChildren<ElementRef<HTMLDivElement>>('pageImage');

  private readonly pageImageService = inject(PageImageService);
  private readonly searchResultNavigator = inject(SearchResultNavigator);
  private readonly pageTracker = inject(PageTracker);
  protected readonly searchService = inject(DocumentSearchService);

  protected readonly fitType: WritableSignal<FitType> = signal(FitType.WIDTH);
  protected readonly currentZoom = signal(1);

  protected readonly isPreviewListExpanded = linkedSignal(() =>
    this.isPreviewListExpandedInitially(),
  );
  protected readonly isPreviewListHovered = signal(false);

  private readonly currentPageId = signal<string | undefined>(undefined);

  private readonly _searchableDocumentIds = computed(
    () => this.searchableDocumentIds() ?? this.allDocuments().map((d) => d.id),
  );

  protected readonly availableDocumentClasses = computed(() => [
    ...new Set(this.allDocuments().flatMap((document) => document.documentClasses) as string[]),
  ]);

  protected readonly selectedDocumentClasses = linkedSignal<string[]>(() => [
    ...this.availableDocumentClasses(),
  ]);

  protected readonly availableBehandlers = computed(() => {
    const behandlers = this.allDocuments()
      .filter((d) => !!d.assignedBehandler)
      .map((d) => d.assignedBehandler as BehandlerItem);

    const lookupTable = associateBy(behandlers, (i) => i.id);

    const list = Object.values(lookupTable).toSorted((a, b) =>
      a.displayName.localeCompare(b.displayName),
    );

    return [...list, NONE_MEDICAL_BEHANDLER_ITEM];
  });

  protected readonly selectedBehandlers = linkedSignal<BehandlerItem[]>(() => [
    ...this.availableBehandlers(),
  ]);

  protected readonly documents = computed(() =>
    this.allDocuments().filter((document) => {
      if (!document.documentClasses.some((dc) => this.selectedDocumentClasses().includes(dc))) {
        return false;
      }

      if (
        !document.assignedBehandler &&
        !this.selectedBehandlers().includes(NONE_MEDICAL_BEHANDLER_ITEM)
      ) {
        return false;
      }

      return !(
        document.assignedBehandler &&
        !this.selectedBehandlers()
          .map((b) => b.id)
          .includes(document.assignedBehandler.id)
      );
    }),
  );

  protected readonly indexedPagesById = computed(
    () =>
      associateBy(
        this.allDocuments()
          .flatMap((document) => document.pages)
          .map((page, index) => ({ ...page, previewIndex: index })),
        (page) => page.id,
      ) as Record<string, CurrentPage>,
  );
  protected readonly totalPageCount = computed(() => Object.keys(this.indexedPagesById()).length);

  protected readonly currentPagePreviewIndex = computed(
    () => this.currentPage()?.previewIndex ?? 0,
  );

  protected readonly currentPage = computed(() => {
    const currentPageId = this.currentPageId();
    return currentPageId ? this.indexedPagesById()[currentPageId] : undefined;
  });

  protected readonly selectedDocumentId = computed(() => {
    const currentPageId = this.currentPageId();
    return currentPageId
      ? (this.documentIdByPageId()?.[currentPageId] ?? this.currentPage()?.documentId)
      : undefined;
  });

  protected readonly filteredIndexedPagesById = computed(
    () =>
      associateBy(
        this.documents()
          .flatMap((document) => document.pages)
          .map((page, index) => ({ ...page, previewIndex: index })),
        (page) => page.id,
      ) as Record<string, CurrentPage>,
  );

  protected readonly isSingleDocumentView = computed(() => !!this.singleOpenedDocument());

  constructor() {
    effect(() => {
      this.allDocuments().forEach((document) => {
        document.documentClasses = document.documentClasses.map((documentClass) =>
          fixDocumentClass(documentClass),
        );
      });

      this.pageImageService.init(this.allDocuments().flatMap((d) => d.pages.map((p) => p.id)));

      if (this.selectInitialPageBehaviour() === 'first') {
        this.currentPageId.set(this.allDocuments()[0]?.pages[0].id);
      }
    });

    effect(() => {
      if (!this.scrollToFirstPageOnInit()) {
        return;
      }

      const pageImages = this.pageImageElements();
      if (pageImages.length) {
        this.scrollToPageByIndex(0);
      }
    });

    effect(() => {
      if (!this.allDocuments()) {
        return;
      }

      this.searchResultNavigator.setPageIds(
        this.allDocuments().flatMap((document) => document.pages.map((page) => page.id)),
      );
    });

    effect(() =>
      this.pageTracker.registerIntersectionRoot(this.pageImageContainer().nativeElement),
    );

    effect(() => {
      const closestPageToCenter = this.pageTracker.closestPageToPreviewCenter();
      if (!closestPageToCenter) {
        return;
      }

      if (this.changeActivePageOnScroll()) {
        this.currentPageId.set(closestPageToCenter);
        this.pageSelected.emit(closestPageToCenter);
      } else {
        this.pageImageService.loadAroundPage(closestPageToCenter);
      }
    });

    effect(() => {
      const selectedId = this.selectedDocumentId();

      if (selectedId) {
        this.documentSelected.emit(selectedId);
      }
    });

    effect(() => {
      const currentPageId = this.currentPageId();

      if (currentPageId) {
        this.pageImageService.loadAroundPage(currentPageId);
      }
    });

    // Without this the target page only enters the load queue once the page tracker
    // notices it, which puts it behind every page scrolled past on the way there.
    effect(() => {
      const pingedWord = this.searchResultNavigator.pingedWord();

      if (pingedWord) {
        this.pageImageService.loadAroundPage(pingedWord.pageId);
      }
    });

    this.copyPageIdOnKeyCombo();
  }

  private copyPageIdOnKeyCombo() {
    fromEvent<KeyboardEvent>(globalThis, 'keydown')
      .pipe(
        takeUntilDestroyed(),
        filter(
          (event) =>
            (event.ctrlKey || event.metaKey) &&
            event.altKey &&
            event.shiftKey &&
            event.code === 'KeyC',
        ),
        map(() => this.currentPageId()),
        filter((x): x is string => !!x),
        switchMap((id) => navigator.clipboard.writeText(id).then(() => id)),
        tap((id) => console.debug(`current page id copied to clipboard. (${id})`)),
      )
      .subscribe();
  }

  ngOnInit() {
    if (this.isPreviewListExpandedInitially()) {
      this.setFitType(() => FitType.WIDTH);
    }
  }

  @HostListener('wheel', ['$event'])
  protected onScroll(event: WheelEvent) {
    if (event.ctrlKey) {
      const zoomMultiplier = 0.002; //50x slower, because it's way too fast
      const zoomAmount = -event.deltaY * zoomMultiplier;
      this.onChangeZoom(zoomAmount);
      event.preventDefault();
    }
  }

  seleDocumentPage(pageId: string, scrollToPage?: boolean) {
    this.currentPageId.set(pageId);
    this.pageSelected.emit(pageId);

    if (scrollToPage) {
      this.scrollToPageById(pageId);
    }
  }

  scrollToPageById(pageId: string) {
    const index = this.filteredIndexedPagesById()[pageId].previewIndex;
    this.scrollToPageByIndex(index);
  }

  private scrollToPageByIndex(pageIndex: number) {
    const imageSection = this.getPageImageSection(pageIndex);
    imageSection.scrollIntoView({
      block: 'center',
      behavior: 'instant',
    });
  }

  private getPageImageSection(index: number): HTMLElement {
    return this.pageImageElements()[index].nativeElement;
  }

  protected onChangeZoom(amount: number) {
    const MAX_ZOOM = 5;
    const MIN_ZOOM = 0.25;
    const container = this.pageImageContainer().nativeElement;

    const relativeXBefore =
      (container.scrollLeft + container.clientWidth * 0.5) / container.scrollWidth;
    const relativeYBefore =
      (container.scrollTop + container.clientHeight * 0.5) / container.scrollHeight;

    this.currentZoom.update((oldZoom) => Math.max(Math.min(oldZoom + amount, MAX_ZOOM), MIN_ZOOM));

    setTimeout(() => {
      const scrollLeft = relativeXBefore * container.scrollWidth - 0.5 * container.clientWidth;
      const scrollTop = relativeYBefore * container.scrollHeight - 0.5 * container.clientHeight;
      container.scrollTo({
        left: scrollLeft,
        top: scrollTop,
      });
    });
  }

  protected togglePreviewList() {
    this.isPreviewListExpanded.update((isExpanded) => !isExpanded);
  }

  protected onToggleFit() {
    this.setFitType((oldType) => (oldType === FitType.WIDTH ? FitType.HEIGHT : FitType.WIDTH));
    this.currentZoom.set(1);
    setTimeout(() => {
      const currentPageId = this.currentPageId();
      if (currentPageId !== undefined) {
        this.scrollToPageById(currentPageId);
      }
    });
  }

  private setFitType(updateFunction: (oldType: FitType) => FitType) {
    this.fitType.update((current) => updateFunction(current));
  }

  protected onPageIntersectionChanged(pageId: string, intersectionEvent: PageIntersectionEvent) {
    if (intersectionEvent.isIntersecting) {
      this.pageTracker.onPageBecameVisible(pageId, intersectionEvent.target);
    } else {
      this.pageTracker.onPageBecameHidden(pageId);
    }
  }

  protected getPageContainerStyle() {
    const zoomedPercent = this.currentZoom() * 100;
    const zoomedCalcValue = `${zoomedPercent}%`;
    const baseLength = this.fitType() === FitType.WIDTH ? 'width' : 'height';
    return {
      [baseLength]: zoomedCalcValue,
    };
  }

  protected getAspectRatio(pageId: string) {
    return this.pageImageService.getImageAspectRatio(pageId)();
  }

  protected onSearchQueried(query: SearchQuery) {
    this.searchService.search({ ...query, documentIds: this._searchableDocumentIds() });
  }

  protected onSearchCleared() {
    this.searchService.clearSearch();
  }

  protected onAppliedDocumentClassesChanged(documentClasses: string[]) {
    this.selectedDocumentClasses.set(documentClasses);
  }

  protected onAppliedBehandlersChanged(behandlers: BehandlerItem[]) {
    this.selectedBehandlers.set(behandlers);
  }
}
